var _ = require("../uam/fields.module.js")(module.exports, {
  namespace: "access",
  title: "Access",
  description: "Normalized access policy object used by pages, files, dashboards, and plugins.",
  type: "field",
  use: {
    namespace: "ua.access"
  },
  fields: {
    type: {
      title: "Access Type",
      type: "string",
      description: "Selected access type.",
      list: {
        must: ["public", "shared-secret", "logged-in", "role", "email", "secure-link"]
      }
    },
    policy: {
      title: "Access Policy",
      type: "string",
      description: "Alias of type used by existing routes and controls.",
      list: {
        must: ["public", "shared-secret", "logged-in", "role", "email", "secure-link"]
      }
    },
    requiredRole: {
      title: "Required Role",
      type: "string",
      description: "Role required when type is role."
    },
    role: {
      title: "Role",
      type: "string",
      description: "Alias of requiredRole for role-based access."
    },
    accessRoles: {
      title: "Access Roles",
      type: "list",
      of: "string",
      description: "Role allow-list. First value can be used as requiredRole."
    },
    emails: {
      title: "Emails",
      type: "list",
      of: "email",
      description: "Email allow-list when type is email."
    },
    sharedSecret: {
      title: "Shared Secret",
      type: "string",
      description: "Raw shared secret for hashing at save time."
    },
    sharedSecretHash: {
      title: "Shared Secret Hash",
      type: "string",
      description: "SHA-256 hash of shared secret."
    },
    secureToken: {
      title: "Secure Token",
      type: "string",
      description: "Secure-link token value."
    },
    secureLinkExpiry: {
      title: "Secure Link Expiry",
      type: "string",
      description: "Secure-link duration: 24h, 7d, 30d, none.",
      list: {
        must: ["24h", "7d", "30d", "none"]
      }
    },
    secureLinkExpiresOn: {
      title: "Secure Link Expires On",
      type: "datetime",
      description: "Resolved secure-link expiration timestamp (YYYYMMDDHHMMSS)."
    }
  },
  errors: {
    invalid: {
      title: "Invalid Access Object",
      description: "The access field must be an object.",
      template: "Expected access object but received ${type}."
    },
    invalidpolicy: {
      title: "Invalid Access Policy",
      description: "The access policy is not supported.",
      template: "Unsupported access policy '${policy}'."
    },
    requiredrole: {
      title: "Role Required",
      description: "A required role is needed for role-based policy."
    },
    requiredemails: {
      title: "Emails Required",
      description: "At least one email is required for email-based policy."
    },
    invalidemail: {
      title: "Invalid Email",
      description: "The access email list contains an invalid email.",
      template: "Invalid email '${email}' in access policy."
    },
    requiredsharedsecret: {
      title: "Shared Secret Required",
      description: "A shared secret or shared secret hash is required."
    }
  }
});

var verifyEmail = require("../uam/functions/verifyEmail.js").function;
var CANONICAL_POLICIES = ["public", "shared-secret", "logged-in", "role", "email", "secure-link"];

function safe(value) {
  return (value || "").toString().trim();
}

function lower(value) {
  return safe(value).toLowerCase();
}

function normalizePolicy(value) {
  var policy = lower(value);
  if (!(policy)) return "";
  if (CANONICAL_POLICIES.indexOf(policy) >= 0) return policy;
  return "";
}

function parseRoleRows(value) {
  if (value instanceof Array) return value.slice();
  if (typeof value === "string") return value.split(/[\n\r,;]+/g);
  if (value === undefined || value === null) return [];
  return [value];
}

function normalizeRoles(value) {
  var rows = parseRoleRows(value);
  var seen = {};
  var next = [];
  for (var i = 0; i < rows.length; i++) {
    var role = lower(rows[i]).replace(/[^a-z0-9._-]/g, "");
    if (!(role) || seen[role]) continue;
    seen[role] = true;
    next.push(role);
  }
  return next;
}

function parseEmailRows(value) {
  if (value instanceof Array) return value.slice();
  if (typeof value === "string") return value.split(/[\n\r,;]+/g);
  if (value === undefined || value === null) return [];
  return [value];
}

function normalizeEmails(value) {
  var rows = parseEmailRows(value);
  var seen = {};
  var next = [];
  for (var i = 0; i < rows.length; i++) {
    var email = lower(rows[i]);
    if (!(email) || seen[email]) continue;
    if (!(verifyEmail(email))) continue;
    seen[email] = true;
    next.push(email);
  }
  return next;
}

function validate(spec, value) {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    _.errors.invalid.throw({ type: typeof value });
  }

  var requestedPolicy = normalizePolicy(value.policy || value.type);
  if (!requestedPolicy) {
    _.errors.invalidpolicy.throw({ policy: safe(value.policy || value.type) });
  }

  if (requestedPolicy === "role") {
    var roles = normalizeRoles(value.accessRoles || value.roles || value.role || value.requiredRole || []);
    if (roles.length < 1) {
      _.errors.requiredrole.throw({ value: value });
    }
  }

  if (requestedPolicy === "email") {
    var normalizedEmails = normalizeEmails(value.emails);
    if (normalizedEmails.length < 1) {
      _.errors.requiredemails.throw({ value: value });
    }
    var rows = parseEmailRows(value.emails);
    for (var i = 0; i < rows.length; i++) {
      var email = safe(rows[i]).toLowerCase();
      if (!(email)) continue;
      if (!(verifyEmail(email))) {
        _.errors.invalidemail.throw({ email: email });
      }
    }
  }

  if (requestedPolicy === "shared-secret") {
    var sharedSecret = safe(value.sharedSecret || "");
    var sharedSecretHash = safe(value.sharedSecretHash || "");
    if (!(sharedSecret) && !(sharedSecretHash)) {
      _.errors.requiredsharedsecret.throw({ value: value });
    }
  }
}
module.exports.validate = validate;
